/**
 * main.tsx — the student app's entry point.
 *
 * The session is resolved BEFORE React mounts. `App` still renders a loading
 * screen if it is asked early (a test, a future entry), but on the real path
 * the first frame the student sees is already the right one.
 */
import { createRoot } from 'react-dom/client'
import { loadSession } from './session'

/* Every tap vibrates, app-wide (packages/device/src/tap-haptics.ts). Loaded off the boot
   chunk: `app/index` sits at its frozen budget and the listener is not needed for first paint. */
void import('@theidiomaclub/device/tap-haptics').then(m => m.installTapHaptics())

/* The eleven local sheets, in order. See `shell/styles/index.css`. */
import '@student/shell/styles/index.css'

/* The card library's own stylesheet. Every rule in it is either a `--ak-*`
   custom property or a selector under `.ak-root`/`.ak-*`, so it cannot shadow —
   and cannot be shadowed by — the unprefixed classnames the eleven sheets use.
   It is already isolated by the library's namespace and needs no wrapper. */
import '@theidiomaclub/sala-clara-card-library/styles.css'

/* LAST, and that is load-bearing. `desktop.css` is the only sheet that adapts
   this front to a big screen, and one of its rules lands on a selector the
   phone CSS above already declares at the same specificity: `.screen`'s top
   padding (`app.css` sets `padding-top: max(12px, env(safe-area-inset-top))`,
   `desktop.css` sets `padding-block: 28px 32px`). At equal specificity the last
   import wins; moved earlier, every heading on a laptop sits 12px from the top
   of the window. See the file's own header. */
import '@student/shell/styles/desktop.css'

/* THE ADULT FRONT RUNS ON OPENAI, END TO END. `voicelab-ai` is Whisper for the
   words, `gpt-4o-mini` for the tutor and `gpt-4o-mini-tts` for the voice — the
   engine Rafael measured in the lab, with the one piece his bench delegated to
   the browser (transcription) done server-side, which is also what makes it work
   on an iPhone. No Gemini and no Vertex on this front.

   Set here rather than inside the speaking screen because it is a property of
   THIS APP, not of one component: whichever surface opens a session, this front
   never talks to Gemini. `apps/legacy` never calls this, so the child tutor
   keeps `sparkly-process` and the Spanish, emoji and streak it was built for.

   FROM `speaking-engine`, NOT FROM `sparkly.service`. The service re-exports
   this setter for convenience, and importing it through there pulled the whole
   service — session CRUD, the audio round trip, and its Zod schemas — into the
   boot graph of every student who never opens the screen. `check-bundle` caught
   it at +12 KB gz on `app/index`. The slot module imports nothing at all.

   Rolling back is deleting these two lines. */
import { setSpeakingEngine } from '@theidiomaclub/sparkly-engine/infra/speaking-engine'
setSpeakingEngine('voicelab-ai')

const container = document.getElementById('root')
if (!container) throw new Error('[student] #root is missing from index.html')

const root = createRoot(container)

/* `App` is a dynamic import on purpose: a signed-out visitor at the door must never download it
   (map, motion, the Episodes gate). The signed-in path starts the same import in parallel with
   the session, so it costs no extra wait. */
function renderApp(): void {
    void import('./App').then(({ App }) => root.render(<App />))
}

type DoorComponent = typeof import('@student/features/join/ui/DoorRoot').DoorRoot

function loadDoorChunk(): Promise<DoorComponent | null> {
    return import('@student/features/join/ui/DoorRoot').then(
        (m) => m.DoorRoot,
        () => {
            console.error('[student] the door chunk did not load')
            return null
        },
    )
}

function waitForOnline(): Promise<void> {
    return new Promise((resolve) => window.addEventListener('online', () => resolve(), { once: true }))
}

/* The static shell stays up; its pill says why the door is not here yet, and the chunk is asked
   again each time the network comes back. */
async function mountDoor(first: Promise<DoorComponent | null>): Promise<void> {
    let Door = await first
    while (!Door) {
        document.getElementById('door-shell')?.classList.add('is-offline')
        await waitForOnline()
        Door = await loadDoorChunk()
    }
    root.render(<Door />)
}

/* `?door=google` is the Google round trip back to the door: index.html leaves `data-door` off
   for it (a code is in flight), but the door is who finishes the exchange. */
const isGoogleReturn = new URLSearchParams(location.search).get('door') === 'google'

if (isGoogleReturn) {
    void mountDoor(loadDoorChunk())
} else if (document.documentElement.dataset.door) {
    /* The door chunk downloads while the session resolves. Fails closed: `loadSession` never
       rejects — it resolves to `signed-out`, which is the door. */
    const door = loadDoorChunk()
    void loadSession().then((session) => {
        if (session.status === 'signed-out') return mountDoor(door)
        renderApp()
    })
} else {
    /* Resolve the session first, then paint. Fails closed: `loadSession` never rejects — it
       resolves to `signed-out`, and `App` mounts the door in place. */
    const app = import('./App')
    /* Opened straight on billing (a reload, a payment link, the paywall): its chunk downloads beside the session
       instead of after the shell mounts (Lighthouse 2026-10-14). Code only — no read leaves before the session. */
    if (location.hash.startsWith('#/plans')) void import('@student/features/billing/ui/BillingFlow')
    void loadSession().finally(() => {
        void app.then(({ App }) => root.render(<App />))
    })
}
